import React, { useEffect, useState, useMemo } from "react"
import { interpolateZoom } from "d3-interpolate"
import { easeQuadInOut } from "d3-ease"
import { Spring } from "react-spring/renderprops"

import MapRenderer from "./MapRenderer"
import { getCenter, scaleProjection } from "../Map/scaleProjection.js"
import { getCountryByCode } from "../utils/countryLookup.js"
import gradient from "../utils/gradient"
import memoize from "lodash/memoize"
import cloneDeep from "lodash/cloneDeep"
import debounce from "lodash/debounce"
import "./styles.scss"
import globeIcon from "../images/globe.svg"
import DataLoader from "../DataLoader"

const standaloneWorld = {}

export function updateTransform(
  width,
  height,
  selectedRegion,
  geography = DataLoader.worldMap,
) {
  if (selectedRegion.length) {
    const target = {
      type: "FeatureCollection",
      features: selectedRegion
        .map(
          (c) => geography.features.find((f) => f.id === c.toString()) || null,
        )
        .filter((c) => c !== null),
    }
    return scaleProjection(target, width, height)
  }
  return scaleProjection(geography, width, height)
}

export function updatePaths(transform, _features, mapId) {
  let features =
    _features || (DataLoader.worldMap ? DataLoader.worldMap.features : null)
  if (!features) {
    return []
  }
  return features.map((country) => {
    if (!country.paths) {
      country.paths = {}
    }
    // country.path = transform.path(country);
    country.paths[mapId] = transform.path(country)
    country.center = getCenter(transform.path, country)
    return country
  })
}

function calculateNewPaths(width, height, selectedRegion, geography, mapId) {
  const transform = updateTransform(width, height, selectedRegion, geography)
  const paths = updatePaths(transform, geography.features, mapId)
  return [paths, transform]
}

const memoizedNewPaths = memoize(
  calculateNewPaths,
  function (width, height, selectedRegion, geography, mapId) {
    const regionIds = selectedRegion.join(",")
    const geoIds = geography.features.map((d) => d.id).join(",")
    return `${width}-${height}-${regionIds}-${geoIds}-${mapId}`
  },
)

function assignData(paths, data, defaultFill) {
  return paths.map((p) => {
    p.info = getCountryByCode(p.properties.WB_A3) || false
    p.data = false
    p.color = defaultFill

    if (p.info) {
      const countryData = data.find((c) => c.code === p.info.code)
      if (countryData) {
        p.data = countryData
        p.value = p.data.value
        p.color = gradient(p.value)
        if (p.data.overrideFill) {
          p.color = p.data.overrideFill
        }
      } else {
        //console.log(`no data for ${p.info.name}`);
      }
    }

    return p
  })
}

let measured = false
const initTransform = { from: [], to: [] }
const transformState = {}

function matchTransform(id) {
  transformState[id].from = measured ? transformState[id].to.slice() : []
}

export function lerp(start, end, time) {
  return start * (1 - time) + end * time
}

export default function Map(props) {
  const [mapSize, setMapSize] = useState({
    width: props.width,
    height: props.height,
  })
  useEffect(() => {
    function _measure() {
      let nextWidth = props.width,
        nextHeight = props.height
      if (props.parent) {
        const parentBounds = props.parent.current.getBoundingClientRect()
        nextWidth = parentBounds.width
        nextHeight = nextWidth * 0.5
      }
      measured = true
      setMapSize({ width: nextWidth, height: nextHeight })
    }
    _measure()
    const debouncedMeasure = debounce(_measure, 100, {
      leading: true,
      trailing: true,
    })
    window.addEventListener("resize", debouncedMeasure)
    return function cleanup() {
      window.removeEventListener("resize", debouncedMeasure)
    }
  }, [props.parent, props.width, props.height])

  if (!standaloneWorld[props.id]) {
    standaloneWorld[props.id] = cloneDeep(DataLoader.worldMap)
    standaloneWorld[props.id].all_features = cloneDeep(
      standaloneWorld[props.id].features,
    )
  }

  const targetGeography = standaloneWorld[props.id]
  targetGeography.features = useMemo(
    () =>
      targetGeography.all_features.filter((d) =>
        props.visibleCountries.length === 0
          ? true
          : props.visibleCountries.includes(d.id),
      ),
    [props.visibleCountries, targetGeography.all_features],
  )

  const { width, height } = mapSize

  const [updatedPaths, transform] = memoizedNewPaths(
    width,
    height,
    props.selectedRegion,
    targetGeography,
    props.id,
  )

  const paths = assignData(
    updatedPaths,
    props.data,
    props.defaultFill,
    props.colorByPercentDifferenceData,
  )
  let map = null
  if (!props.animate) {
    const { translate, scale } = transform
    map = (
      <MapRenderer
        width={width}
        height={height}
        colorByData={true}
        paths={paths}
        selectedLabels={[]}
        strokedCountries={props.strokedCountries}
        customStroke={props.customStroke}
        defaultStroke={props.stroke}
        defaultFill={props.defaultFill}
        countryOpacity={1}
        labelOpacity={1}
        pointOpacity={0}
        translate={translate}
        scale={scale}
        gotoCountryPage={props.gotoCountryPage}
        tooltipEnabled={props.tooltipEnabled}
        tooltipMessage={props.tooltipMessage}
        id={props.id}
      />
    )
  } else {
    const useWidth = transform.dx / width > transform.dy / height
    if (!transformState[props.id]) {
      transformState[props.id] = Object.assign({}, initTransform)
    }
    transformState[props.id].to = [
      transform.x,
      transform.y,
      useWidth ? transform.dx : transform.dy,
      useWidth ? width : height,
    ]
    if (!transformState[props.id].from.length) {
      matchTransform(props.id)
    }
    const fromTransform = transformState[props.id].from.length
      ? transformState[props.id].from
      : transformState[props.id].to
    const interpolator = interpolateZoom(
      fromTransform,
      transformState[props.id].to,
    )

    map = interpolator ? (
      <Spring
        config={{
          duration: 1225,
          easing: easeQuadInOut,
        }}
        reset
        from={{ t: 0 }}
        to={{ t: 1 }}
        onStart={() => matchTransform(props.id)}
        onRest={() => matchTransform(props.id)}
      >
        {({ t }) => {
          const view = interpolator(t)
          const size = lerp(fromTransform[3], transformState[props.id].to[3], t)
          const scale = 0.9 * (size / view[2])
          const translate = [
            width / 2 - view[0] * scale,
            height / 2 - view[1] * scale,
          ]
          return (
            <MapRenderer
              width={width}
              height={height}
              colorByData={true}
              paths={paths}
              selectedLabels={[]}
              strokedCountries={props.strokedCountries}
              customStroke={props.customStroke}
              defaultStroke={props.stroke}
              defaultFill={props.defaultFill}
              countryOpacity={1}
              labelOpacity={1}
              pointOpacity={0}
              translate={translate}
              scale={scale}
              gotoCountryPage={props.gotoCountryPage}
              tooltipEnabled={props.tooltipEnabled}
              tooltipMessage={props.tooltipMessage}
              showPercentDifferenceLabel={props.showPercentDifferenceLabel}
              selectedYear={props.selectedYear}
              overflow={props.overflow}
              id={props.id}
            />
          )
        }}
      </Spring>
    ) : null
  }

  const clickGlobeIcon = () => {
    props.gotoCountryPage("")()
  }
  let globeButton = null
  if (props.allowZoomOut && props.selectedRegion.length) {
    const targetWidth = width - 20
    const targetHeight = height - 10
    const scaledWidth = width * props.mapScale
    const scaledHeight = height * props.mapScale
    const translateX = (targetWidth - scaledWidth) / 2 //- 10
    const translateY = targetHeight - scaledHeight // - 10
    const globeButtonStyle = {
      transform: `scale(${
        1 / props.mapScale
      }) translate(${-translateX}px, ${translateY}px)`,
    }
    globeButton = (
      <div
        onClick={clickGlobeIcon}
        style={globeButtonStyle}
        className="globeButton"
      >
        <img src={globeIcon} alt="Zoom out" />{" "}
      </div>
    )
  }
  return (
    <div style={{ width, height, position: "relative", ...props.style }}>
      {map}
      {globeButton}
    </div>
  )
}

Map.defaultProps = {
  id: "defaultMap",
  animate: false,
  width: 1280,
  height: 640,
  stroke: "white",
  customStroke: { color: "#333", width: 1 },
  defaultFill: "#f0f0f0",
  selectedRegion: [],
  visibleCountries: [],
  strokedCountries: [],
  data: [],
  gotoCountryPage: () => {},
  tooltipEnabled: true,
  allowZoomOut: false,
  showPercentDifferenceLabel: false,
}
